<template>
  <v-dialog :value="value" max-width="520" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="text-h6">
        {{ isEdit ? $t('surveys.campaigns.edit') : $t('surveys.campaigns.new') }}
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="form.name"
            :label="$t('surveys.campaigns.form.name')"
            :rules="[requiredRule]"
            counter="255"
            maxlength="255"
          />

          <v-text-field
            v-model="form.startsAt"
            :label="$t('surveys.campaigns.form.starts_at')"
            :rules="[requiredRule, dateOrderRule]"
            type="date"
          />
          <v-text-field
            v-model="form.endsAt"
            :label="$t('surveys.campaigns.form.ends_at')"
            :rules="[requiredRule, dateOrderRule]"
            type="date"
          />
          <p class="text-caption grey--text mb-0">
            {{ $t('surveys.campaigns.form.dates_hint') }}
          </p>

          <v-autocomplete
            v-model="form.countryIds"
            :items="countries"
            item-value="id"
            item-text="name"
            multiple
            small-chips
            deletable-chips
            :label="$t('surveys.campaigns.form.countries')"
            class="mt-4"
          />
          <p class="text-caption grey--text mb-0">
            {{ form.countryIds.length
              ? $t('surveys.campaigns.form.countries_hint')
              : $t('surveys.campaigns.all_countries') }}
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">
          {{ $t('surveys.campaigns.form.cancel') }}
        </v-btn>
        <v-btn color="deep-purple" dark :loading="saving" @click="save">
          {{ $t('surveys.campaigns.form.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import surveysService from '@/services/surveys.service';
import snackbarMixin from '@/mixins/snackbar';

const emptyForm = () => ({
  name: '',
  startsAt: '',
  endsAt: '',
  countryIds: [],
});

export default {
  name: 'CampaignFormDialog',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    audience: { type: String, required: true },
    campaign: { type: Object, default: null },
    countries: { type: Array, default: () => [] },
  },
  data: () => ({
    form: emptyForm(),
    valid: false,
    saving: false,
  }),
  computed: {
    isEdit() {
      return Boolean(this.campaign);
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.form = this.campaign
        ? {
          name: this.campaign.name,
          startsAt: this.campaign.startsAt || '',
          endsAt: this.campaign.endsAt || '',
          countryIds: [...(this.campaign.countryIds || [])],
        }
        : emptyForm();
      this.$nextTick(() => this.$refs.form?.resetValidation());
    },
    requiredRule(value) {
      return Boolean(value) || this.$t('surveys.campaigns.form.required');
    },
    dateOrderRule() {
      if (!this.form.startsAt || !this.form.endsAt) return true;
      return (
        this.form.startsAt <= this.form.endsAt
        || this.$t('surveys.campaigns.form.dates_order')
      );
    },
    payload() {
      return {
        name: this.form.name,
        startsAt: this.form.startsAt,
        endsAt: this.form.endsAt,
        countryIds: this.form.countryIds,
      };
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        if (this.isEdit) {
          await surveysService.updateCampaign(this.audience, this.campaign.id, this.payload());
          this.snackbarSuccess(this.$t('surveys.campaigns.updated'));
        } else {
          await surveysService.createCampaign(this.audience, this.payload());
          this.snackbarSuccess(this.$t('surveys.campaigns.created'));
        }
        this.$emit('saved');
        this.close();
      } catch (error) {
        // 409 = fechas solapadas con otra campaña de la misma audiencia. El
        // backend lo rechaza porque, con dos campañas vigentes, no queda
        // claro cuál debería estar sirviendo la encuesta.
        const message = error?.response?.status === 409
          ? this.$t('surveys.campaigns.overlap')
          : this.$t('general.error_msg');
        this.snackbarError(message);
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>
